import { ChangeEvent, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ResourceCategoryTypes } from "@enums/resource-category-types";
import {
    AXNUILayout,
    AXNUITheme,
    AXNUIThemeGeneral
} from "@iddataweb/idw-common-ui/types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { FileInputFormGroup } from "../../../../../common/file-input-form-group/file-input-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingLayoutGeneralSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    inheritedAXNUILayout: AXNUILayout;
    inheritedAXNUITheme: AXNUITheme;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUIThemeGeneralFieldValue: (key: keyof AXNUIThemeGeneral, value: string) => void;
    updateAXNUILayoutPropertyValue: (value: AXNUILayout) => void;
    pageLevel: PageLevelTypes;
};

export const ThemingLayoutGeneralSection = (props: ThemingLayoutGeneralSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiLayout,
        axnuiTheme,
        inheritedAXNUILayout,
        inheritedAXNUITheme,
        uploadFileToS3,
        updateAXNUILayoutPropertyValue,
        updateAXNUIThemeGeneralFieldValue,
        pageLevel
    } = props;

    const [faviconUploadInProgress, setFaviconUploadInProgress] = useState(false);

    const handleLayoutChange = (key: keyof AXNUILayout, value: boolean) => {
        const newPropertyValue: AXNUILayout = {
            ...axnuiLayout,
            [key]: value
        };
        updateAXNUILayoutPropertyValue(newPropertyValue);
    };

    const handleAddPageHeaderChange = (newValue: string) => {
        handleLayoutChange("isHeaderEnabled", isStringTrue(newValue));
    };

    const handleResetPageHeader = () => {
        handleLayoutChange("isHeaderEnabled", inheritedAXNUILayout.isHeaderEnabled);
    };

    const handleAddPageFooterChange = (newValue: string) => {
        handleLayoutChange("isFooterEnabled", isStringTrue(newValue));
    };

    const handleResetPageFooter = () => {
        handleLayoutChange("isFooterEnabled", inheritedAXNUILayout.isFooterEnabled);
    };

    const handleFaviconChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
            //upload to s3
            setFaviconUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, ResourceCategoryTypes.FAVICON);
            if (uploadResult) {
                updateAXNUIThemeGeneralFieldValue("faviconUrl", uploadResult);
            }
            setFaviconUploadInProgress(false);
        }
    };

    const handleResetFavicon = () => {
        updateAXNUIThemeGeneralFieldValue("faviconUrl", inheritedAXNUITheme.general.faviconUrl || "");
    };

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUILayout.isHeaderEnabled}
                currentThemingPropertyValue={axnuiLayout.isHeaderEnabled}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-layout-page-header-radio-input`}
                    automationId={`theming-layout-page-header-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.inputs.addPageHeader.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddPageHeaderChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiLayout.isHeaderEnabled ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.inputs.addPageHeader.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetPageHeader}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUILayout.isFooterEnabled}
                currentThemingPropertyValue={axnuiLayout.isFooterEnabled}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-layout-page-footer-radio-input`}
                    automationId={`theming-layout-page-footer-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.inputs.addPageFooter.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddPageFooterChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiLayout.isFooterEnabled ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.inputs.addPageFooter.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetPageFooter}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.faviconUrl}
                currentThemingPropertyValue={axnuiTheme.general.faviconUrl}
                pageLevel={pageLevel}
            >
                <FileInputFormGroup
                    controlId={"theming-layout-favicon-input"}
                    automationId={"theming-layout-favicon-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.inputs.favicon.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.inputs.favicon.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFaviconChange}
                    value={axnuiTheme.general.faviconUrl || ""}
                    accept={"image/png, image/gif, image/jpeg, image/jpg"}
                    disabled={isDisabled}
                    uploadInProgress={faviconUploadInProgress}
                    isRequired={false}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.inputs.favicon.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFavicon}
                />
            </ThemingFormGroupWrapper>
        </div>
    );
};